<template>
	<div class="w-full max-w-3xl mx-auto">
		<!-- Header Card -->
		<div class="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
			<!-- Decorative top bar -->
			<div class="h-2 bg-primary"></div>

			<div class="p-8">
				<div class="flex items-start justify-between mb-6">
					<div class="flex-1">
						<div class="flex items-center gap-3 mb-2">
							<div
								class="w-12 h-12 rounded-xl bg-primary text-white flex items-center justify-center font-bold text-lg shadow-md"
							>
								{{ valuerInitials }}
							</div>
							<div>
								<h2
									class="text-2xl font-bold text-text-primary"
								>
									{{ valuer.name }}
								</h2>
								<p class="text-sm text-text-secondary">
									Valuation Progress Tracker
								</p>
							</div>
						</div>
					</div>

					<!-- Current Status Badge -->
					<div class="flex flex-col items-end gap-2">
						<div
							class="inline-flex items-center gap-2 px-4 py-2 rounded-xl shadow-sm"
							:class="statusBadgeClass"
						>
							<div
								class="w-2 h-2 rounded-full animate-pulse"
								:class="statusDotClass"
							></div>
							<span class="text-sm font-bold">{{
								currentStatusLabel
							}}</span>
						</div>
						<div class="text-xs text-text-secondary">
							Current Status
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Progress Timeline Card -->
		<div class="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
			<div class="p-8">
				<div class="flex items-center gap-3 mb-8">
					<div
						class="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"
					>
						<svg
							class="w-5 h-5 text-primary"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
							></path>
						</svg>
					</div>
					<div>
						<h3 class="text-lg font-bold text-text-primary">
							Progress Timeline
						</h3>
						<p class="text-sm text-text-secondary">
							Track the valuation journey
						</p>
					</div>
				</div>

				<div class="relative">
					<!-- Vertical connecting line -->
					<div
						class="absolute left-6 top-0 bottom-0 w-0.5 bg-border"
						style="top: 24px; bottom: 24px"
					></div>

					<div class="space-y-8">
						<div
							v-for="(step, index) in steps"
							:key="step.key"
							class="relative flex items-start gap-6"
						>
							<!-- Step indicator -->
							<div class="relative z-10 flex-shrink-0">
								<div
									class="w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-500 shadow-md"
									:class="stepIconClass(step.key)"
								>
									<svg
										v-if="isStepCompleted(step.key)"
										class="w-6 h-6"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="3"
											d="M5 13l4 4L19 7"
										></path>
									</svg>
									<svg
										v-else-if="isStepCurrent(step.key)"
										class="w-6 h-6"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
										></path>
									</svg>
									<div
										v-else
										class="w-3 h-3 rounded-full bg-current"
									></div>
								</div>
							</div>

							<!-- Step content -->
							<div class="flex-1 pt-2 pb-4">
								<div
									class="bg-surface border border-border rounded-xl p-5 transition-all duration-300 hover:shadow-md"
									:class="stepCardClass(step.key)"
								>
									<div
										class="flex items-start justify-between gap-4 mb-2"
									>
										<div>
											<h4
												class="text-lg font-bold text-text-primary mb-1"
											>
												{{ step.label }}
											</h4>
											<p
												class="text-sm text-text-secondary"
											>
												{{
													getStepDescription(step.key)
												}}
											</p>
										</div>
										<span
											class="text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap"
											:class="stepStatusBadge(step.key)"
										>
											{{ statusText(step.key) }}
										</span>
									</div>

									<div class="flex items-center gap-2 mt-3">
										<svg
											class="w-4 h-4 text-text-secondary"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
											></path>
										</svg>
										<span
											class="text-sm font-medium text-text-secondary"
										>
											{{
												formatDate(getDate(step.key)) ||
												'Not started yet'
											}}
										</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Summary Card -->
		<div class="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
			<div class="p-8">
				<div class="flex items-start gap-3 mb-4">
					<div
						class="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0"
					>
						<svg
							class="w-5 h-5 text-accent"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
							></path>
						</svg>
					</div>
					<div>
						<h3 class="text-lg font-bold text-text-primary mb-1">
							Valuation Summary
						</h3>
						<p class="text-sm text-text-secondary">
							Overview of the valuation details
						</p>
					</div>
				</div>

				<div class="bg-surface border border-border rounded-xl p-5">
					<p class="text-sm text-text-primary leading-relaxed">
						{{
							valuer.summary ||
							'No summary available yet. The summary will be updated as the valuation progresses.'
						}}
					</p>
				</div>
			</div>
		</div>

		<!-- Action Buttons -->
		<div class="bg-white rounded-2xl shadow-lg overflow-hidden">
			<div class="p-6">
				<div class="flex items-center justify-between gap-4">
					<button
						class="px-6 py-3 text-sm font-bold bg-surface text-text-primary rounded-xl hover:bg-border border-2 border-border hover:border-primary/30 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all duration-200 active:scale-95"
						@click="$emit('close')"
						aria-label="Close valuation progress"
					>
						Close
					</button>
					<button
						class="flex-1 max-w-xs px-6 py-3 text-sm font-bold bg-primary hover:bg-primary-light text-white rounded-xl shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all duration-200 active:scale-95 flex items-center justify-center gap-2"
						@click="$emit('view-summary', valuer)"
						aria-label="View valuation summary"
					>
						<svg
							class="w-4 h-4"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
							></path>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
							></path>
						</svg>
						View Full Summary
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'ValuationProgress',
	props: {
		valuer: {
			type: Object,
			required: true,
		},
		status: {
			type: String,
			default: 'started',
		},
		startedDate: {
			type: [String, Date],
			default: '',
		},
		endDate: {
			type: [String, Date],
			default: '',
		},
	},
	data() {
		return {
			steps: [
				{ key: 'started', label: 'Started' },
				{ key: 'processing', label: 'Processing' },
				{ key: 'approved', label: 'Approved' },
				{ key: 'completed', label: 'Completed' },
			],
		};
	},
	computed: {
		currentStatusLabel() {
			if (!this.status) return '';
			return this.status.charAt(0).toUpperCase() + this.status.slice(1);
		},
		valuerInitials() {
			if (!this.valuer || !this.valuer.name) return '';
			const parts = this.valuer.name.split(' ').filter(Boolean);
			let first = '';
			let second = '';
			if (parts.length > 0 && parts[0] && parts[0].length > 0)
				first = parts[0][0];
			if (parts.length > 1 && parts[1] && parts[1].length > 0)
				second = parts[1][0];
			return (first + second).toUpperCase();
		},
		statusBadgeClass() {
			const classes = {
				started: 'bg-blue-50 text-blue-700 border border-blue-200',
				processing:
					'bg-yellow-50 text-yellow-700 border border-yellow-200',
				approved: 'bg-green-50 text-green-700 border border-green-200',
				completed:
					'bg-primary/10 text-primary border border-primary/20',
			};
			return classes[this.status] || classes.started;
		},
		statusDotClass() {
			const classes = {
				started: 'bg-blue-500',
				processing: 'bg-yellow-500',
				approved: 'bg-green-500',
				completed: 'bg-primary',
			};
			return classes[this.status] || classes.started;
		},
	},
	methods: {
		stepIconClass(step) {
			if (this.isStepCompleted(step)) {
				return 'bg-primary text-white';
			} else if (this.isStepCurrent(step)) {
				return 'bg-accent text-white';
			}
			return 'bg-surface text-text-secondary border-2 border-border';
		},
		stepCardClass(step) {
			if (this.isStepCurrent(step)) {
				return 'border-accent/30 bg-accent/5';
			} else if (this.isStepCompleted(step)) {
				return 'border-primary/20 bg-primary/5';
			}
			return '';
		},
		stepStatusBadge(step) {
			if (this.isStepCompleted(step)) {
				return 'bg-primary/10 text-primary';
			} else if (this.isStepCurrent(step)) {
				return 'bg-accent/10 text-accent';
			}
			return 'bg-surface text-text-secondary';
		},
		isStepCompleted(step) {
			const order = this.steps.map((s) => s.key);
			const currentIndex = order.indexOf(this.status);
			const stepIndex = order.indexOf(step);
			return stepIndex < currentIndex;
		},
		isStepCurrent(step) {
			return step === this.status;
		},
		statusText(step) {
			if (this.isStepCompleted(step)) return 'Completed';
			if (this.isStepCurrent(step)) return 'In Progress';
			return 'Pending';
		},
		getStepDescription(step) {
			const descriptions = {
				started: 'Valuation process has been initiated',
				processing: 'Currently evaluating and analyzing data',
				approved: 'Valuation has been reviewed and approved',
				completed: 'All procedures completed successfully',
			};
			return descriptions[step] || '';
		},
		getDate(step) {
			const dates = {
				started: this.startedDate,
				processing: this.valuer.processingDate,
				approved: this.valuer.approvedDate,
				completed: this.endDate,
			};
			return dates[step];
		},
		formatDate(d) {
			if (!d) return '';
			try {
				const date = new Date(d);
				return date.toLocaleDateString('en-US', {
					month: 'short',
					day: 'numeric',
					year: 'numeric',
				});
			} catch (e) {
				return d;
			}
		},
	},
};
</script>

<style scoped>
@keyframes pulse {
	0%,
	100% {
		opacity: 1;
	}
	50% {
		opacity: 0.5;
	}
}

.animate-pulse {
	animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style>
